import React, { useEffect } from 'react';
import { string } from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { copy } from '@thd-olt-functional/utils';
import { useStoreId } from '@thd-nucleus/experience-context';
import { useProduct, useReviews } from '~/@thd-nucleus/data-sources';
import { ProductStructuredData, BreadcrumbStructuredData, VideoStructuredData } from './structured-data';

function formatDescription(description) {
  if (!description) return '';
  const MAX_LEN = 199;
  if (description.length < MAX_LEN || description[MAX_LEN] === ' ') return description;
  let nextSpace = description.indexOf(' ', MAX_LEN);
  return description.substring(0, nextSpace);
}

const ProductMetadata = (props) => {
  const { itemId } = props;
  const storeId = useStoreId();

  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('product-metadata.ready'); }, []);

  const { data, loading, error } = useProduct({
    itemId,
    storeId,
    domains: [
      'identifiers',
      'details',
      'media',
      'taxonomy',
      'availabilityType',
      'reviews',
      'pricing',
      'specificationGroup'
    ],
    alias: 'Metadata'
  });
  const { data: reviewData, loading: reviewLoading, reviewError } = useReviews({ itemId });

  if (reviewError && !reviewData) return null;
  if (reviewLoading) return null;
  if (!reviewData) return null;

  if (error && !data) return null;
  if (loading) return null;
  if (!data) return null;

  const {
    details,
    identifiers,
    seoDescription,
    seoKeywords,
    availabilityType,
    media,
    taxonomy,
    reviews,
    pricing,
    specificationGroup
  } = data?.product || {};

  // eslint-disable-next-line max-len
  const title = `${identifiers?.brandName || ''} ${identifiers?.productLabel || ''}-${identifiers?.modelNumber || ''} - The Home Depot`.trim();
  const description = formatDescription(seoDescription || details?.description);
  const canonical = `https://www.homedepot.com${identifiers?.canonicalUrl}`;
  const alternate = `android-app://com.thehomedepot/homedepot/product/${itemId}`;
  const { video, image } = media || {};
  const primaryImage = image?.url;
  let robotsTag;
  if (availabilityType?.discontinued) {
    robotsTag = <meta name="robots" content="noindex" />;
  }
  return (
    <>
      <Helmet>
        <meta name="description" content={description} />
        <meta name="keywords" content={seoKeywords} />
        <meta name="og:description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={primaryImage} />
        <meta property="og:type" content="product" />
        <meta property="og:site_name" content="The Home Depot" />
        <meta property="fb:admins" content="100005416960152" />
        {robotsTag}
        <title>{title}</title>
        <link rel="canonical" href={canonical} />
        <link rel="alternate" href={alternate} />
      </Helmet>
      <ProductStructuredData
        aggregateReviews={reviews?.ratingsReviews}
        reviews={reviewData?.reviews?.Results}
        identifiers={identifiers}
        media={media}
        details={details}
        pricing={pricing}
        specifications={specificationGroup}
      />
      <BreadcrumbStructuredData
        canonical={canonical}
        breadcrumbs={copy(taxonomy?.breadCrumbs || []).data}
        title={title}
      />
      { !!video?.length
        && (
          <VideoStructuredData
            videos={video}
            productLabel={identifiers?.productLabel || ''}
          />
        )}
    </>
  );
};

ProductMetadata.propTypes = {
  itemId: string.isRequired
};

export { ProductMetadata };
